import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { captureSmartTablePresetCreated } from "./analytics";
import {
  CreateBrandVisibilityPresetRequest,
  CreateBrandVisibilityPresetResponse,
  CreateCustomPresetRequest,
  CreateCustomPresetResponse,
  CreateEvaluationPresetRequest,
  CreateEvaluationPresetResponse,
  CreateModelComparisonPresetRequest,
  CreateModelComparisonPresetResponse,
  SmartSheet,
  unwrapSmartTableEntityPayload,
} from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayToast } from "@/utils/toast";

interface UseInitializeSheetPresetOptions {
  tableId?: string;
  sheetId?: string;
}

const presetSuccessMessage = (presetType: CreateEvaluationPresetRequest["preset_type"]) =>
  presetType === "GROUND_TRUTH_COMPARE"
    ? "Ground truth check initialized"
    : "Structure check initialized";

const invalidateInitializedSheet = async ({
  queryClient,
  wid,
  tableId,
  sheetId,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
  wid: number;
  tableId: string;
  sheetId: string;
}) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["smart-tables", wid, tableId, "bootstrap"] }),
    queryClient.invalidateQueries({ queryKey: ["smart-tables", wid, tableId, "sheets", sheetId] }),
    queryClient.invalidateQueries({ queryKey: ["smart-tables", wid, tableId, sheetId, "columns"] }),
    queryClient.invalidateQueries({ queryKey: ["smart-tables", wid, tableId, sheetId, "score"] }),
    queryClient.invalidateQueries({
      queryKey: ["smart-tables", wid, tableId, sheetId, "status-counts"],
    }),
  ]);
};

export const useInitializeSheetModelComparisonPreset = ({
  tableId,
  sheetId,
}: UseInitializeSheetPresetOptions) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();

  return useMutation<SmartSheet, unknown, CreateModelComparisonPresetRequest>({
    mutationFn: async payload => {
      if (!tableId || !sheetId) {
        throw new Error("Missing sheet target");
      }

      const response = await fetch(
        ENDPOINTS.smart_table_sheet_model_comparison_preset(wid, tableId, sheetId),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify(payload),
        },
      );
      const responsePayload = (await handleResponse(
        response,
      )) as CreateModelComparisonPresetResponse;
      return unwrapSmartTableEntityPayload<SmartSheet>(responsePayload, "sheet");
    },
    onSuccess: async (sheet, payload) => {
      if (!tableId || !sheetId) {
        return;
      }

      await invalidateInitializedSheet({ queryClient, wid, tableId, sheetId });
      captureSmartTablePresetCreated({
        workspaceId: wid,
        smartTableId: tableId,
        presetType: "model-comparison",
        promptRegistryId: payload.prompt_registry_id,
        promptVersionNumber: payload.prompt_version_number,
      });
      displayToast(`${sheet.title} initialized`, ToastType.success);
    },
  });
};

export const useInitializeSheetEvaluationPreset = ({
  tableId,
  sheetId,
}: UseInitializeSheetPresetOptions) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();

  return useMutation<SmartSheet, unknown, CreateEvaluationPresetRequest>({
    mutationFn: async payload => {
      if (!tableId || !sheetId) {
        throw new Error("Missing sheet target");
      }

      const response = await fetch(
        ENDPOINTS.smart_table_sheet_evaluation_preset(wid, tableId, sheetId),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify(payload),
        },
      );
      const responsePayload = (await handleResponse(response)) as CreateEvaluationPresetResponse;
      return unwrapSmartTableEntityPayload<SmartSheet>(responsePayload, "sheet");
    },
    onSuccess: async (_sheet, payload) => {
      if (!tableId || !sheetId) {
        return;
      }

      await invalidateInitializedSheet({ queryClient, wid, tableId, sheetId });
      captureSmartTablePresetCreated({
        workspaceId: wid,
        smartTableId: tableId,
        presetType: payload.preset_type,
        promptRegistryId: payload.prompt_registry_id,
        promptVersionNumber: payload.prompt_version_number,
      });
      displayToast(presetSuccessMessage(payload.preset_type), ToastType.success);
    },
  });
};

export const useInitializeSheetCustomPreset = ({
  tableId,
  sheetId,
}: UseInitializeSheetPresetOptions) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();

  return useMutation<SmartSheet, unknown, CreateCustomPresetRequest>({
    mutationFn: async payload => {
      if (!tableId || !sheetId) {
        throw new Error("Missing sheet target");
      }

      const response = await fetch(
        ENDPOINTS.smart_table_sheet_custom_preset(wid, tableId, sheetId),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify(payload),
        },
      );
      const responsePayload = (await handleResponse(response)) as CreateCustomPresetResponse;
      return unwrapSmartTableEntityPayload<SmartSheet>(responsePayload, "sheet");
    },
    onSuccess: async (sheet, payload) => {
      if (!tableId || !sheetId) {
        return;
      }

      await invalidateInitializedSheet({ queryClient, wid, tableId, sheetId });
      captureSmartTablePresetCreated({
        workspaceId: wid,
        smartTableId: tableId,
        presetType: "custom",
        promptRegistryId: payload.prompt_registry_id,
        promptVersionNumber: payload.prompt_version_number,
      });
      displayToast(`${sheet.title} initialized`, ToastType.success);
    },
  });
};

export const useInitializeSheetBrandVisibilityPreset = ({
  tableId,
  sheetId,
}: UseInitializeSheetPresetOptions) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();

  return useMutation<SmartSheet, unknown, CreateBrandVisibilityPresetRequest>({
    mutationFn: async payload => {
      if (!tableId || !sheetId) {
        throw new Error("Missing sheet target");
      }

      const response = await fetch(
        ENDPOINTS.smart_table_sheet_brand_visibility_preset(wid, tableId, sheetId),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify(payload),
        },
      );
      const responsePayload = (await handleResponse(
        response,
      )) as CreateBrandVisibilityPresetResponse;
      return unwrapSmartTableEntityPayload<SmartSheet>(responsePayload, "sheet");
    },
    onSuccess: async sheet => {
      if (!tableId || !sheetId) {
        return;
      }

      await invalidateInitializedSheet({ queryClient, wid, tableId, sheetId });
      captureSmartTablePresetCreated({
        workspaceId: wid,
        smartTableId: tableId,
        presetType: "brand-visibility",
      });
      displayToast(
        sheet.title ? `${sheet.title} initialized` : "Brand visibility initialized",
        ToastType.success,
      );
    },
  });
};
